import { Fragment, ReactNode, useState } from 'react';
import { Typography } from '../Typography';
import { Button } from '../../atoms/Button';
import { Icon } from '../../atoms/Icon';
import DropDown from '../Dropdown';
import './styles.css';
import { LoadingIcon } from 'morpheus-ui';
import InnerHTMLContainer from '../InnerHTMLContainer';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import HowToScoreCard from '../../atoms/HowToScoreCard';
import { BadgeScore } from '../../atoms/BadgeScore';

interface MenuItem {
  link: string;
  label: string;
}

interface IPageHeader {
  module?: string;
  title: string;
  children?: ReactNode;
  className?: string;
  isDropdown?: boolean;
  dropdownItems?: MenuItem[];
  isDiscipline?: boolean;
  loading?: boolean;
  shouldShowHowToScoreCard?: boolean;
  showScore?: boolean | (string | number)[] | string | number;
  badgeTypeScore?: 'warning' | 'success' | 'error' | undefined;
}

export default function PageHeader({
  children,
  title,
  module,
  className,
  isDropdown = false,
  dropdownItems,
  isDiscipline = false,
  loading = false,
  shouldShowHowToScoreCard = false,
  showScore = false,
  badgeTypeScore,
}: IPageHeader) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { user } = useUserStore();

  const selectStyle = (item: MenuItem) => {
    if (item.label && title) {
      const normalizedTitle = title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      const normalizedLabel = item.label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      if (normalizedTitle.toLowerCase() === normalizedLabel.toLowerCase()) {
        return 'selected';
      }

      return 'focusable';
    }

    return 'focusable';
  };

  const loadingDecider = () => {
    if (isDiscipline) {
      return loading;
    }

    return !title;
  };

  const shouldShowScore =
    typeof showScore === 'number' ||
    Array.isArray(showScore) ||
    showScore === true;

  return (
    <div className='flex w-full flex-col justify-between gap-xs md:flex-row'>
      <div className='order-2 flex w-full flex-col gap-xs md:order-1'>
        <Typography size='h6' type='titleSection'>
          {module}
        </Typography>
        {!isDropdown ? (
          <div className='flex w-full flex-row items-center gap-2'>
            <Typography size='h2' type='title' className={className}>
              <InnerHTMLContainer data={title} />
            </Typography>
            {shouldShowScore && (
              <BadgeScore score={showScore} type={badgeTypeScore} />
            )}
          </div>
        ) : (
          <Fragment>
            <div className='flex w-fit flex-row items-center gap-sm'>
              {/* <Button
              size='regular'
              variant='text'
              className='p-0 capitalize text-mono-color-black'
              onClick={() => setIsOpen(!isOpen)}
              data-tooltip-id={`header-dropdown`}
            > */}
              {loadingDecider() ? (
                <div className='flex w-full items-center justify-center'>
                  <LoadingIcon className='stroke-current h-4 animate-spin fill-none text-center' />
                </div>
              ) : (
                <Typography
                  onClick={() => setIsOpen(!isOpen)}
                  size='h2'
                  type='title'
                  className={`${className} cursor-pointer`}
                  data-tooltip-id={`header-dropdown`}
                >
                  {title}
                </Typography>
              )}
              {isOpen ? (
                <Icon
                  onClick={() => setIsOpen(!isOpen)}
                  Icon='expand_less'
                  type='material'
                  className=' cursor-pointer text-mono-color-black'
                />
              ) : (
                <Icon
                  onClick={() => setIsOpen(!isOpen)}
                  Icon='expand_more'
                  type='material'
                  className=' cursor-pointer  text-mono-color-black'
                />
              )}
              {/* </Button> */}
            </div>
            <DropDown
              id={`header-dropdown`}
              position='bottom-start'
              arrow
              onClick
              afterHide={(status) => setIsOpen(status)}
              isOpen={isOpen}
              style={
                user?.isDarkMode
                  ? { backgroundColor: '#000C29', zIndex: 200 }
                  : { backgroundColor: '#ffffff', zIndex: 200 }
              }
              //@todo: ver como utilizar os design tokens}
            >
              <div
                id='dropContent'
                className='max-h-[45vh] overflow-y-auto xs:w-fit md:w-[360px]'
              >
                {dropdownItems &&
                  dropdownItems.map((it, i) => {
                    return (
                      <Button
                        allowReadOnly
                        key={i}
                        variant={selectStyle(it)}
                        onClick={() => {
                          setIsOpen(false);
                        }}
                        asLink
                        to={it.link}
                        className='w-full text-left font-semibold'
                      >
                        {it.label}
                      </Button>
                    );
                  })}
              </div>
            </DropDown>
          </Fragment>
        )}
        {children}
      </div>
      {shouldShowHowToScoreCard && (
        <div className='order-1 flex w-full justify-center md:order-2'>
          <HowToScoreCard />
        </div>
      )}
    </div>
  );
}
